import { ContractTypeUnion } from '../../../../../types';
import { NO_DATA_TEMPLATE } from '../../../constants/strings/contract/form-constants';
import {
  BillableActivityType,
  ContractType,
  CostShareOptionInputType,
  MilestoneConditionType,
} from '../../../types';
import { ActiveStatus } from '../../../utils/contract-utils';
import { IDiscount } from '../../../utils/discount-utils';
import { CurrencyOption } from './components/currency-select';

export enum ValidBillingModel {
  ENGAGEMENT = 'engagement',
  STANDARD = 'standard',
  INDIVIDUAL_MILESTONE = 'individual_milestone',
  COHORT_MILESTONE = 'cohort_milestone',
  FEE_FOR_SERVICE = 'fee_for_service',
  ADMIN_FEE_CLAIMS = 'admin_fee_claims',
}

export interface BillableActivityDetails {
  [key: string]: number;
}

export interface BillableActivitiesWithDetails {
  [key: number]: BillableActivityDetails | null;
}

export interface RawInnerPackageFormValues {
  id: number | null;
  billingModelId: number;
  packageId: number;
  price: number;
  initialFee: number;
  activityFee: number;
  feePercent: number;
  selectedBillableActivities: BillableActivitiesWithDetails;
  currentTemplate: Partial<ContractTemplateOption>;
  costShareRequired: boolean;
  deductionTiming: string | null;
  cadence: string | null;
  billingTypeOverride: string | null;
  startDate: Date | null;
}

export interface RawContractPackageFormValues {
  startDate: Date | null;
  endDate: Date | null;
  userAnnualCap: number;
  globalDailyEngagementCap: number | null;
  currency: CurrencyOption;
  selectedPackages: RawInnerPackageFormValues[];
  isVoid: boolean;
  partnershipId: number | null;
  billingType: string | null;
}

export interface ContractPackageFormValues {
  startDate: string;
  endDate: string | null;
  void: boolean;
  userAnnualCap: number;
  globalDailyEngagementCap: number | null;
  currency: string;
  contractPackages: ContractInnerPackageFormValues[];
  partnershipId: number | null;
  billingType: string | null;
}

export interface ContractPackageFormProps {
  currentContracts: ContractType[];
  activeStatus?: ActiveStatus;
  editId?: number;
  onSubmitCallback: (values: ContractPackageFormValues) => void;
  onCancel: () => void;
  disableSubmit: boolean;
  hideVoid: boolean;
  submitTitle: string;
  wholeFormDisabled?: boolean;
  startDateDisablePast?: boolean;
  partnershipId?: number;
  isBatchCreate?: boolean;
  billingType?: string | null;
  discounts?: IDiscount[];
}

export interface PackageFormData {
  selectedPackages: {
    id: number | null;
    packageId: number;
    billingModelId: number;
    price: number;
    initialFee: number;
    activityFee: number;
    selectedBillableActivities: BillableActivitiesWithDetails;
    currentTemplate: ContractTemplateOption;
  }[];
  selectedBillableActivities: BillableActivitiesWithDetails;
}

export type NewBillableActivityTypeObject = Record<
  number,
  BillableActivityDetails | null
>;

export interface ContractInnerPackageFormValues {
  id: number | null;
  packageId: number;
  billingModelId: number;
  price: number;
  startDate: string;
  rules?:
    | PackageRules
    | AdminFeePackageRules
    | ContractTypeUnion[]
    | Record<string, unknown>[]
    | null;
  contractTemplateId?: number | null;
  billableActivityTypes: number[] | NewBillableActivityTypeObject;
  billingTypeOverride?: string | null;
  costShareOptions?: CostShareOptionInputType;
}

export interface PackageRules {
  activationFee: number;
  activityFee: number;
}

export interface AdminFeePackageRules {
  feePercent: number;
}

export const DEFAULT_FORM_CURRENCY = {
  label: 'USD $',
  value: 'USD',
};

export const DEFAULT_PACKAGE_FORM_VALUES = {
  id: null,
  billingModelId: 0,
  packageId: 0,
  price: 1750.0,
  initialFee: 250.0,
  activityFee: 50.0,
  feePercent: 0,
  selectedBillableActivities: {},
  currentTemplate: NO_DATA_TEMPLATE,
  costShareRequired: false,
  deductionTiming: null,
  cadence: null,
  billingTypeOverride: null,
  startDate: null,
};

export const DEFAULT_FORM_VALUES = {
  startDate: null,
  endDate: null,
  userAnnualCap: 1750.0,
  globalDailyEngagementCap: null,
  currency: DEFAULT_FORM_CURRENCY,
  isVoid: false,
  partnershipId: null,
  selectedPackages: [DEFAULT_PACKAGE_FORM_VALUES],
  billingType: null,
};

export interface ContractTemplateOption {
  name: string;
  contractTemplateId: number;
  contract: ContractTypeUnion[] | Record<string, unknown>[];
}

export type ContractTypeExtension = ContractTypeUnion & {
  name: string;
  payment: number;
  billableActivityTypes: string[] | BillableActivityType[];
  dateOfServiceOffset?: number;
  dayRange: number[];
  conditions: MilestoneConditionType[];
};
